import { useState, useEffect } from "react";
import { Users, Building2, PhoneCall, MessageSquare, Eye, Key, TrendingUp } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { ADMIN } from "../../constants/testIds";

export default function AdminDashboard() {
  const { adminAxios } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAxios.get("/admin/dashboard/stats")
      .then(({ data }) => setStats(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageShell><div className="animate-pulse text-zinc-400 text-sm">Loading...</div></PageShell>;

  return (
    <PageShell>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Scarlet Properties — Control Centre</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Active Users" value={stats?.active_users ?? 0} icon={<Users size={16} strokeWidth={1.5} />} testId={ADMIN.statsActiveUsers} color="text-zinc-900" />
        <StatCard label="Active Codes" value={stats?.active_codes ?? 0} icon={<Key size={16} strokeWidth={1.5} />} />
        <StatCard label="Properties" value={stats?.total_properties ?? 0} icon={<Building2 size={16} strokeWidth={1.5} />} testId={ADMIN.statsProperties} />
        <StatCard label="Total Views" value={stats?.total_views ?? 0} icon={<Eye size={16} strokeWidth={1.5} />} />
        <StatCard label="New Leads" value={stats?.new_leads ?? 0} icon={<PhoneCall size={16} strokeWidth={1.5} />} testId={ADMIN.statsNewLeads} highlight={stats?.new_leads > 0} />
        <StatCard label="New Inquiries" value={stats?.new_inquiries ?? 0} icon={<MessageSquare size={16} strokeWidth={1.5} />} testId={ADMIN.statsNewInquiries} highlight={stats?.new_inquiries > 0} />
        <StatCard label="Published" value={stats?.published_properties ?? 0} icon={<TrendingUp size={16} strokeWidth={1.5} />} />
        <StatCard label="Draft" value={stats?.draft_properties ?? 0} icon={<Building2 size={16} strokeWidth={1.5} />} />
      </div>

      {/* Property Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">Property Status</h3>
          <div className="space-y-3">
            {[
              { label: "Published", value: stats?.published_properties ?? 0, color: "bg-green-500" },
              { label: "Draft", value: stats?.draft_properties ?? 0, color: "bg-yellow-400" },
              { label: "Archived", value: stats?.archived_properties ?? 0, color: "bg-zinc-300" },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 ${color}`} />
                  <span className="text-sm text-zinc-600">{label}</span>
                </div>
                <span className="text-sm font-medium text-zinc-900">{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">Leads & Inquiries</h3>
          <div className="space-y-3">
            {[
              { label: "Total Leads", value: stats?.total_leads ?? 0 },
              { label: "New Leads", value: stats?.new_leads ?? 0 },
              { label: "Total Inquiries", value: stats?.total_inquiries ?? 0 },
              { label: "New Inquiries", value: stats?.new_inquiries ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">{label}</span>
                <span className="text-sm font-medium text-zinc-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">Recent Activity</h3>
        {stats?.recent_activity?.length === 0 ? (
          <p className="text-zinc-400 text-sm">No recent activity.</p>
        ) : (
          <div className="space-y-2">
            {(stats?.recent_activity || []).map((log) => (
              <div key={log.id} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
                <div>
                  <span className="text-sm text-zinc-700">{log.user_name}</span>
                  <span className="text-xs text-zinc-400 ml-2">{log.action}</span>
                  {log.details && <span className="text-xs text-zinc-400 ml-1">— {log.details}</span>}
                </div>
                <span className="text-xs text-zinc-400">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}

function StatCard({ label, value, icon, testId, highlight }) {
  return (
    <div className={`bg-white border p-5 ${highlight ? "border-[#C8102E]/30" : "border-zinc-200"}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-zinc-400">{icon}</span>
        {highlight && <div className="w-2 h-2 bg-[#C8102E] rounded-full" />}
      </div>
      <div className={`text-2xl font-semibold ${highlight ? "text-[#C8102E]" : "text-zinc-900"}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5 tracking-wide">{label}</div>
    </div>
  );
}

function PageShell({ children }) {
  return <div className="p-8 min-h-screen bg-zinc-50">{children}</div>;
}
